import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, ArrowUpRight } from 'lucide-react';
import Reveal from './ui/Reveal';

const About: React.FC = () => {
  const { t } = useTranslation();
  const [imgFailed, setImgFailed] = useState(false);

  const facts = [
    { label: t('about.facts.location'), value: 'Damascus, Syria' },
    { label: t('about.facts.experience'), value: '4+ ' + t('hero.stats.years').toLowerCase() },
    { label: t('about.facts.focus'), value: 'Flutter · ASP.NET' },
    { label: t('about.facts.availability'), value: t('contact.info.availability') },
  ];

  const highlights = [
    { title: t('about.highlights.crossPlatform.title'), desc: t('about.highlights.crossPlatform.desc') },
    { title: t('about.highlights.performance.title'), desc: t('about.highlights.performance.desc') },
    { title: t('about.highlights.delivered.title'), desc: t('about.highlights.delivered.desc') },
    { title: t('about.highlights.clean.title'), desc: t('about.highlights.clean.desc') },
  ];

  return (
    <section id="about" className="section">
      <div className="container-page">
        <Reveal>
          <p className="eyebrow">{t('about.eyebrow')}</p>
          <h2 className="section-title mt-4 max-w-2xl">{t('about.title')}</h2>
          <p className="mt-5 max-w-prose text-graphite">{t('about.subtitle')}</p>
        </Reveal>

        <Reveal className="mt-10 grid gap-10 lg:grid-cols-5 lg:gap-14">
          {/* Portrait + facts */}
          <div className="lg:col-span-2">
            <div className="overflow-hidden border border-hairline bg-plate" style={{ borderRadius: 2 }}>
              <div className="relative aspect-[4/5]">
                {imgFailed ? (
                  <span
                    className="ui absolute inset-0 flex items-center justify-center text-[13px] font-semibold uppercase tracking-[0.22em] text-graphite"
                    aria-hidden="true"
                  >
                    Anas Jammal
                  </span>
                ) : (
                  <picture>
                    <img
                      src="/projects/profile.jpg"
                      alt="Anas Jammal"
                      loading="lazy"
                      decoding="async"
                      width={640}
                      height={900}
                      onError={() => setImgFailed(true)}
                      className="absolute inset-0 h-full w-full object-cover object-center"
                    />
                  </picture>
                )}
              </div>
            </div>

            <dl className="mt-8">
              {facts.map(({ label, value }) => (
                <div key={label} className="row-ruled grid-cols-[auto_1fr] items-baseline">
                  <dt className="ui text-[12px] font-semibold uppercase tracking-[0.16em] text-graphite">
                    {label}
                  </dt>
                  <dd className="ui text-right text-[15px] font-medium text-ink">{value}</dd>
                </div>
              ))}
            </dl>

            {/* Third-party proof, so it is stated as a plate with a way to verify it
                rather than as one more unsourced row in the facts list above. The
                stars are decorative — the rating and the count are both in text, so
                nothing is carried by the glyphs alone. */}
            <div
              className="mt-8 border border-hairline bg-plate p-5"
              style={{ borderRadius: 2 }}
            >
              <p className="eyebrow">{t('about.mostaql.label')}</p>
              <p className="mt-3 flex items-center gap-2.5">
                <span aria-hidden="true" className="flex gap-0.5 text-lacquer-bright">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                  ))}
                </span>
                <span className="ui text-[18px] font-bold leading-none text-ink">
                  {t('about.mostaql.rating')}
                </span>
              </p>
              <p className="mt-2.5 text-[14px] leading-relaxed text-graphite">
                {t('about.mostaql.summary')}
              </p>
              <a
                href="https://mostaql.com/u/anasaljammal"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-quiet mt-4 w-full"
              >
                {t('about.mostaql.cta')}
                <ArrowUpRight size={15} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Bio + highlights */}
          <div className="lg:col-span-3">
            <h3 className="ui max-w-xl text-[26px] font-bold leading-[1.2] tracking-[-0.015em] text-ink sm:text-[32px]">
              {t('about.heading')}
            </h3>
            <p className="mt-6 max-w-prose leading-relaxed text-graphite">
              {t('about.description')}
            </p>
            <p className="mt-4 max-w-prose leading-relaxed text-graphite">{t('about.details')}</p>

            {/* Ruled rows rather than four more cards — a card means "a project". */}
            <dl className="mt-10 grid sm:grid-cols-2 sm:gap-x-10">
              {highlights.map(({ title, desc }) => (
                <div key={title} className="row-ruled">
                  <dt className="ui text-[15px] font-bold text-ink">{title}</dt>
                  <dd className="text-[15px] leading-relaxed text-graphite">{desc}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default About;
